import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { authApi } from '../api';
import { useAuthStore } from '../store/authStore';
import EyeIcon from '../components/common/EyeIcon';

const LOCK_MS = 10 * 60 * 1000;
const MAX_FAILS = 5;

const getFailEntry = (email: string) => {
  try {
    const d = JSON.parse(localStorage.getItem(`lf_${email}`) || 'null');
    return (d as { count: number; lockUntil: number }) || { count: 0, lockUntil: 0 };
  } catch {
    return { count: 0, lockUntil: 0 };
  }
};

const saveFailEntry = (email: string, count: number, lockUntil: number) =>
  localStorage.setItem(`lf_${email}`, JSON.stringify({ count, lockUntil }));

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [lockRemain, setLockRemain] = useState(0);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  // 이메일 변경 시 실패/잠금 상태 동기화
  useEffect(() => {
    if (!form.email) { setFailCount(0); setLockRemain(0); return; }
    const { count, lockUntil } = getFailEntry(form.email);
    setFailCount(count);
    const remain = Math.ceil((lockUntil - Date.now()) / 1000);
    setLockRemain(remain > 0 ? remain : 0);
  }, [form.email]);

  // 1초마다 카운트다운
  useEffect(() => {
    if (lockRemain <= 0) return;
    const id = setTimeout(() => setLockRemain(r => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(id);
  }, [lockRemain]);

  const isLocked = lockRemain > 0;
  const lockMin = Math.floor(lockRemain / 60);
  const lockSec = lockRemain % 60;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) return;
    setError('');

    const { count, lockUntil } = getFailEntry(form.email);
    if (Date.now() < lockUntil) {
      setLockRemain(Math.ceil((lockUntil - Date.now()) / 1000));
      return;
    }

    if (!form.email || !form.password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.login(form);
      const { accessToken, refreshToken, userId, ...rest } = res.data.data;
      localStorage.removeItem(`lf_${form.email}`);
      setAuth({ ...rest, id: userId } as any, accessToken);
      navigate(redirectTo);
    } catch (err: any) {
      const newCount = count + 1;
      const newLock = newCount >= MAX_FAILS ? Date.now() + LOCK_MS : lockUntil;
      saveFailEntry(form.email, newCount, newLock);
      setFailCount(newCount);
      if (newCount >= MAX_FAILS) {
        setLockRemain(Math.ceil(LOCK_MS / 1000));
      } else {
        setError('Incorrect email or password.');
      }
      setForm(f => ({ ...f, password: '' }));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true);
    setError('');
    try {
      const res = await authApi.socialLogin('GOOGLE', credentialResponse.credential);
      const { accessToken, userId, ...rest } = res.data.data;
      setAuth({ ...rest, id: userId } as any, accessToken);
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Google login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src="/symbol.png" alt="Moa" style={styles.symbol} />
        <h1 style={styles.title}>{t('auth.loginTitle')}</h1>
        <p style={styles.sub}>{t('auth.loginSub')}</p>

        {/* 잠금 안내 */}
        {isLocked && (
          <div style={styles.lockBanner}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Too many failed attempts. Login locked.
            </div>
            <div>
              Try again in{' '}
              <strong>{lockMin}:{String(lockSec).padStart(2, '0')}</strong>
              , or{' '}
              <Link to="/forgot-password" style={{ color: '#c0392b', fontWeight: 600 }}>
                reset your password
              </Link>
              .
            </div>
          </div>
        )}

        {/* 일반 에러 */}
        {error && !isLocked && (
          <div style={styles.error}>
            {error}
            {failCount >= 3 && failCount < MAX_FAILS && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                {MAX_FAILS - failCount} attempt(s) remaining before lockout.
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          <div style={styles.field}>
            <label style={styles.label}>{t('auth.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder={t('auth.emailPlaceholder')}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>{t('auth.password')}</label>
            <div style={styles.passwordWrap}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={t('auth.passwordPlaceholder')}
                style={{ ...styles.input, paddingRight: 44, width: '100%', boxSizing: 'border-box' }}
                required
                disabled={isLocked}
              />
              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              ><EyeIcon open={showPassword} /></button>
            </div>
            <div style={styles.forgotRow}>
              <Link to="/forgot-id" style={styles.forgotLink}>Forgot ID?</Link>
              <Link to="/forgot-password" style={styles.forgotLink}>Forgot Password?</Link>
            </div>
          </div>
          <button
            type="submit"
            style={{ ...styles.btn, opacity: isLocked ? 0.5 : 1 }}
            disabled={loading || isLocked}
          >
            {loading ? t('auth.loggingIn') : isLocked
              ? `Locked (${lockMin}:${String(lockSec).padStart(2, '0')})`
              : t('auth.loginBtn')}
          </button>
        </form>

        <div style={styles.divider}><span style={styles.dividerText}>또는</span></div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google login was cancelled.')}
            text="signin_with"
            shape="rectangular"
            width="340"
          />
        </div>

        <p style={styles.footer}>
          {t('auth.noAccount')} <Link to="/register" style={styles.link}>{t('auth.goRegister')}</Link>
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' },
  card: { background: '#fff', padding: '48px 40px', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  symbol: { width: 72, height: 72, display: 'block', margin: '0 auto 12px' },
  title: { fontSize: 24, fontWeight: 700, color: '#333', textAlign: 'center', marginBottom: 8 },
  sub: { fontSize: 13, color: '#999', textAlign: 'center', marginBottom: 32 },
  error: { background: '#fff0f0', border: '1px solid #ffcdd2', borderRadius: 8, padding: '12px 16px', color: '#e53935', fontSize: 13, marginBottom: 20 },
  lockBanner: { background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '14px 16px', color: '#856404', fontSize: 13, marginBottom: 20, lineHeight: 1.6 },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: '#555' },
  input: { padding: '12px 16px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none' },
  passwordWrap: { position: 'relative' },
  eyeBtn: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#888', display: 'flex', alignItems: 'center' },
  forgotRow: { display: 'flex', justifyContent: 'flex-end', gap: 20, marginTop: 6 },
  forgotLink: { fontSize: 12, color: '#999', textDecoration: 'none' },
  btn: { padding: '14px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, marginTop: 8, cursor: 'pointer' },
  divider: { textAlign: 'center', margin: '24px 0', position: 'relative', borderTop: '1px solid #e8e8e8' },
  dividerText: { background: '#fff', padding: '0 12px', color: '#aaa', fontSize: 12, position: 'relative', top: -10 },
  footer: { textAlign: 'center', fontSize: 13, color: '#888', marginTop: 24 },
  link: { color: '#b39ddb', fontWeight: 600 },
};

export default LoginPage;
